/**
 * PDF Archive — Fase 6 (Phase 1G).
 *
 * ⛔ BACKEND-NYA SELESAI SEJAK 30 JULI 2026 DAN LAYARNYA NOL PERNAH ADA.
 *
 * Empat endpoint `/api/pdf` hidup, 543 dokumen tersimpan, dan satu-satunya
 * cara membukanya selama sebulan adalah lewat tautan yang kebetulan muncul di
 * layar lain. Nol ada tempat untuk pertanyaan yang paling sering diajukan:
 * *"PO bulan lalu yang mana ya?"*
 *
 * ---------------------------------------------------------------------------
 * DUA HAL YANG MEMBENTUK LAYAR INI, DAN KEDUANYA BUKAN SOAL TAMPILAN
 * ---------------------------------------------------------------------------
 *
 * 1. **Tautan diminta SAAT DITEKAN, bukan saat daftar dimuat.** Tautan
 *    berkasnya berumur 5-15 menit (bucket private, nol ada permanent public
 *    link). Mengambil 50 sekaligus berarti hampir semuanya mati sebelum ada
 *    yang menekannya.
 *
 * 2. **Dokumen SUPERSEDED ditandai KERAS, bukan disamarkan.** Ia terlihat
 *    identik dengan yang aktif — judul sama, nomor sama, tampak sah. Yang
 *    mencetaknya sedang bekerja dari angka LAMA, dan nol ada di berkas PDF-nya
 *    yang memberitahunya. Karena itu penandanya di layar, di baris yang sama,
 *    bukan di keterangan bawah.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  apiErrorMessage,
  getPdfSignedUrl,
  labelJenis,
  LABEL_JENIS,
  listPdfDocuments,
} from "@/lib/pdfArchiveApi";
import { formatDateTime } from "@/lib/format";
import Hint from "@/components/etams/Hint";
import { useDaftar } from "@/hooks/useDaftar";
import { useAuth } from "@/contexts/AuthContext";
import { FA_ISI_ROLES } from "@/lib/roles";
import { PalangDaftar, ThUrut, Paginasi } from "@/components/etams/PalangDaftar";

//: Cermin `URUT_SAH_PDF` di backend/routers/pdf_archive.py.
const URUT_SAH = ["generated_at", "title", "kind"];

const STATUS_LABEL = { ACTIVE: "Berlaku", SUPERSEDED: "Sudah digantikan", SEMUA: "Semua" };

const KOLOM_CSV = [
  { kunci: "title", judul: "Dokumen", ambil: (d) => d.title || "" },
  { kunci: "kind", judul: "Jenis", ambil: (d) => labelJenis(d.kind) },
  { kunci: "generated_at", judul: "Dibuat", ambil: (d) => (d.generated_at ? formatDateTime(d.generated_at) : "") },
  { kunci: "status", judul: "Status", ambil: (d) => (d.status === "SUPERSEDED" ? "Digantikan" : "Berlaku") },
];

function Lencana({ status }) {
  if (status === "SUPERSEDED") {
    return (
      <span className="whitespace-nowrap rounded-md border border-neutral-soft-border bg-neutral-soft px-2 py-0.5 text-xs font-semibold text-neutral-soft-foreground">
        Digantikan
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap rounded-md border border-success-soft-border bg-success-soft px-2 py-0.5 text-xs font-medium text-success-soft-foreground">
      Berlaku
    </span>
  );
}

// Arsip PDF — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
// Paginasi buatan sendiri diganti pola standar: cari judul, urut, saringan jenis / status /
// tanggal, 25/50/100 per halaman, dan posisi di alamat. Saringan status bawaan "Berlaku"
// tetap (lihat catatan kepala berkas) tetapi kini terlihat sebagai chip. Menghapusnya =
// SEMUA status — dulu pilihan "Semua" diam-diam jatuh ke Berlaku di server.
export default function PdfArchive() {
  const { profile } = useAuth();
  const isiFa = FA_ISI_ROLES.includes(profile?.role?.code);
  const [baris, setBaris] = useState([]);
  const [total, setTotal] = useState(0);
  const [muat, setMuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [membuka, setMembuka] = useState(null);

  const daftar = useDaftar(baris, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "generated_at", arah: "desc" },
    saringan: {
      jenis: { label: "Jenis", param: "kind", tampil: (v) => labelJenis(v) },
      status: { label: "Status", param: "status", bawaan: "ACTIVE", tampil: (v) => STATUS_LABEL[v] || v },
      dari: { label: "Dari tanggal", param: "date_from" },
      sampai: { label: "Sampai tanggal", param: "date_to" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    // Status dihapus orang = semua status; nilai kosong di server berarti "Berlaku".
    status: paramSaringan.status || "SEMUA",
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const { data } = await listPdfDocuments(bangunParams({ offset: off, limit: n }));
    return data;
  }, [bangunParams]);

  const muatData = useCallback(async () => {
    setMuat(true);
    setGalat("");
    try {
      const { data } = await listPdfDocuments(bangunParams());
      setBaris(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setGalat(apiErrorMessage(e));
      setBaris([]);
    } finally {
      setMuat(false);
    }
  }, [bangunParams]);

  useEffect(() => {
    muatData();
  }, [muatData]);

  /**
   * ⚠ Tab dibuka DULU, baru tautannya diisi.
   *
   * `window.open()` yang dipanggil SESUDAH `await` diblokir peramban sebagai
   * popup — ia kehilangan kaitan dengan klik penggunanya. Akibatnya tombolnya
   * "tidak melakukan apa-apa", dan nol ada galat di mana pun yang menjelaskan
   * kenapa.
   */
  const buka = async (doc) => {
    setMembuka(doc.id);
    setGalat("");
    const tab = window.open("", "_blank");
    try {
      const { data } = await getPdfSignedUrl(doc.id);
      const url = data.signed_url || data.url;
      if (!url) throw new Error("Tautan berkas kosong");
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (e) {
      if (tab) tab.close();
      setGalat(apiErrorMessage(e));
    } finally {
      setMembuka(null);
    }
  };

  const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Arsip PDF</h1>
        <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
          Semua dokumen yang pernah dibuat sistem.
          <Hint>Wilayah dan cabang yang Anda lihat mengikuti peran Anda.</Hint>
        </p>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari judul dokumen" testId="pdf-palang"
                    ekspor={{ nama: "arsip_pdf", kolom: KOLOM_CSV, ambilHalaman }}>
        <select className={sel} value={daftar.nilaiSaringan.jenis} aria-label="Saring jenis"
                onChange={(e) => daftar.setSaringan("jenis", e.target.value)} data-testid="pdf-filter-jenis">
          <option value="">Semua jenis</option>
          {/* K206 / VONIS-HAK-BACA-ISI-FA: dokumen customer (kwitansi, tagihan FA, surat jalan) hanya dikirim backend
              ke Owner/KC/CS — pilihannya tidak ditawarkan ke peran lain (selalu kosong bagi mereka). */}
          {Object.entries(LABEL_JENIS)
            .filter(([k]) => isiFa || !k.startsWith("kwitansi"))
            .map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
        </select>
        {/* ⚠ Bawaannya BERLAKU, dan itu disengaja — lihat catatan kepala
            berkas. Melihat yang digantikan wajib jadi pilihan sadar. */}
        <select className={sel} value={daftar.nilaiSaringan.status} aria-label="Saring status"
                onChange={(e) => daftar.setSaringan("status", e.target.value)} data-testid="pdf-filter-status">
          <option value="ACTIVE">Berlaku</option>
          <option value="SUPERSEDED">Sudah digantikan</option>
          <option value="">Semua</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          Tanggal
          <input type="date" className={sel} value={daftar.nilaiSaringan.dari} aria-label="Dari tanggal"
                 onChange={(e) => daftar.setSaringan("dari", e.target.value)} />
          s/d
          <input type="date" className={sel} value={daftar.nilaiSaringan.sampai} aria-label="Sampai tanggal"
                 onChange={(e) => daftar.setSaringan("sampai", e.target.value)} />
        </label>
      </PalangDaftar>

      {galat && (
        <div role="alert" className="rounded-lg border border-danger-soft-border bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground">
          {galat}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="min-w-full text-sm">
          <thead className="border-b border-border bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <ThUrut daftar={daftar} kunci="title" className="px-4 py-3">Dokumen</ThUrut>
              <ThUrut daftar={daftar} kunci="kind" className="px-4 py-3">Jenis</ThUrut>
              <ThUrut daftar={daftar} kunci="generated_at" className="px-4 py-3">Dibuat</ThUrut>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Berkas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {muat && daftar.tampil.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Memuat…</td></tr>
            )}
            {!muat && daftar.tampil.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  Tidak ada dokumen yang cocok dengan saringan ini.
                </td>
              </tr>
            )}
            {daftar.tampil.map((d) => (
              <tr key={d.id} className="hover:bg-muted">
                <td className="px-4 py-3 font-medium text-foreground">
                  {d.title || "—"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{labelJenis(d.kind)}</td>
                <td className="px-4 py-3 tabular-nums text-muted-foreground">
                  {d.generated_at ? formatDateTime(d.generated_at) : "—"}
                </td>
                <td className="px-4 py-3"><Lencana status={d.status} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    disabled={membuka === d.id}
                    onClick={() => buka(d)}
                    className="rounded-lg border border-input px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {membuka === d.id ? "Membuka…" : "Buka"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="pdf-paginasi" />
      </div>
    </div>
  );
}
